import CardLoadingState from 'crm-cards-sdk/components/CardLoadingState';
import { NotImplementedCard } from 'crm-cards-sdk/components/NotImplementedCard';
import { DEFAULT_LOADING_COMPONENT_PROPS } from 'crm-cards-sdk/constants/LoadingComponentProps';
import { useCardRenderStarted } from 'crm-cards-sdk/hooks/useCardRenderMarkers';
import { useI18nLoader } from 'crm-cards-sdk/hooks/useI18nLoader';
import { useCardContainerContext } from 'crm-cards-sdk/internal/CardContainerContext';
import { CardDataContextProvider } from 'crm-cards-sdk/internal/CardDataContext';
import { useCardLocationContext } from 'crm-cards-sdk/internal/CardLocationContext';
import { useDispatch as usePerfStoreDispatch } from 'crm-cards-sdk/internal/perf/LocationStateContext';
import { useAreInitialCardsReady, useSelectPerfStateForCard } from 'crm-cards-sdk/internal/perf/cardPerfSelectors';
import { useIsIntersecting } from 'crm-cards-sdk/internal/useIsIntersecting';
import { isLegacyCard } from 'crm-cards-sdk/utils/isLegacyCard';
import mark from 'crm-fe-perf/timing/mark';
import { getCardRenderStart, getCardWrapperRenderStart } from 'crm-fe-perf/timing/marks/crm-cards-sdk/performanceMarks';
import { CrmMessagingContext } from 'crm-message-bus/CrmMessagingContext';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import IframedCrmCard from './IframedCrmCard';
import CardErrorBoundary from './internal/CardErrorBoundary';
import CardTranslationsLoader from './internal/CardTranslationsLoader';
import { useDataResolver } from './internal/useDataResolver';
import { CardSizeContextProvider } from 'crm-cards-sdk/internal/CardSizeContext';
import { jsx as _jsx } from "react/jsx-runtime";
function isPrototypeCard(cardData) {
  return cardData.definition.cardType === 'PROTOTYPE';
}
export default function CrmCardWrapper({
  onCardRenderFinished,
  registry,
  usageTracker,
  cardData,
  CardComponentOverride,
  tabIndex
}) {
  const [cardWrapperRef, setCardWrapperRef] = useState(null);
  const cardLocationContext = useCardLocationContext();
  const {
    location,
    loggingTag
  } = cardLocationContext;
  const {
    DefaultCardFormatter,
    ActionFormatter
  } = useCardContainerContext();
  const i18nLoader = useI18nLoader();
  const isInViewport = useIsIntersecting(cardWrapperRef, true);
  const initialCardsAreReady = useAreInitialCardsReady();
  const [isRenderedOffscreen, setIsRenderedOffscreen] = useState(false);
  const {
    offscreenCardsPolicy
  } = cardLocationContext;
  const cardPerfState = useSelectPerfStateForCard({
    cardType: cardData.definition.cardType,
    cardId: cardData.definition.cardId,
    tabIndex
  });
  const hasCalledCardRenderFinished = useRef(false);
  const shouldRenderCard = isInViewport || !cardLocationContext.enableVirtualization || isRenderedOffscreen;
  const resolvedDataState = useDataResolver({
    registry,
    cardData,
    shouldRenderCard
  });
  useEffect(() => {
    if (onCardRenderFinished && cardPerfState !== null && cardPerfState !== void 0 && cardPerfState.finishedState && !hasCalledCardRenderFinished.current) {
      // only call the finished callback once
      hasCalledCardRenderFinished.current = true;
      onCardRenderFinished({
        cardId: cardData.definition.cardId,
        cardType: cardData.definition.cardType,
        finishedState: cardPerfState === null || cardPerfState === void 0 ? void 0 : cardPerfState.finishedState,
        isCollapsed: cardPerfState === null || cardPerfState === void 0 ? void 0 : cardPerfState.isCollapsed,
        tabIndex,
        type: 'CARD_LOADING_FINISHED'
      });
    }
  }, [cardData.definition.cardId, cardData.definition.cardType, cardPerfState === null || cardPerfState === void 0 ? void 0 : cardPerfState.finishedState, cardPerfState === null || cardPerfState === void 0 ? void 0 : cardPerfState.isCollapsed, onCardRenderFinished, tabIndex, hasCalledCardRenderFinished]);
  useEffect(() => {
    if (initialCardsAreReady && (offscreenCardsPolicy === null || offscreenCardsPolicy === void 0 ? void 0 : offscreenCardsPolicy.type) === 'AFTER_INITIAL_LOAD') {
      const timeoutId = setTimeout(() => setIsRenderedOffscreen(true), offscreenCardsPolicy.delay || 0);
      return () => clearTimeout(timeoutId);
    }
  }, [initialCardsAreReady, offscreenCardsPolicy]);

  // Start preloading JS and Lang modules
  useLayoutEffect(() => {
    if (registry.preloadCardModules && shouldRenderCard) {
      registry.preloadCardModules(cardData.definition.cardType, i18nLoader, {
        location,
        logging_tag: loggingTag
      });
    }
  }, [location, loggingTag, cardData.definition.cardType, i18nLoader, registry, shouldRenderCard]);
  const exactCardType = isLegacyCard(cardData) ? cardData.configuration.cardType : cardData.definition.cardType;
  // Using a ref to avoid rerender in memoized card variable
  const dimensionsRef = useRef({
    location,
    logging_tag: loggingTag || '',
    card_type: exactCardType
  });
  useEffect(() => {
    mark(getCardWrapperRenderStart(exactCardType, cardData.definition.cardId), dimensionsRef.current, true);
  }, [cardData.definition.cardId, exactCardType]);
  const dispatch = usePerfStoreDispatch();
  useEffect(() => {
    dispatch({
      type: 'CARD_WRAPPER_RENDERED'
    });
  }, [dispatch]);
  const loadingComponentProps = useMemo(() => {
    try {
      return registry.getLoadingComponentProps(cardData.definition.cardType, cardData, cardLocationContext);
    } catch (error /** card not found */) {
      return DEFAULT_LOADING_COMPONENT_PROPS;
    }
  }, [cardData, cardLocationContext, registry]);
  const cardContextValue = useMemo(() => ({
    cardData,
    isInViewport,
    isRenderedOffscreen,
    loadingComponentProps,
    tabIndex
  }), [cardData, isInViewport, isRenderedOffscreen, loadingComponentProps, tabIndex]);
  const messagingContextValue = useMemo(() => ({
    messagingId: cardData.definition.cardId
  }), [cardData.definition.cardId]);
  useCardRenderStarted(exactCardType, cardData.definition.cardId, isInViewport, isRenderedOffscreen, tabIndex);
  const isInitiallyVisible = useRef(null);
  if (isInitiallyVisible.current === null && isInViewport !== undefined) {
    isInitiallyVisible.current = isInViewport;
  }
  const card = useMemo(() => {
    if (!shouldRenderCard) {
      return /*#__PURE__*/_jsx(CardLoadingState, {
        pastDelay: false /* always show empty div */,
        CardFormatter: DefaultCardFormatter
      });
    }
    mark(getCardRenderStart(exactCardType, cardData.definition.cardId), Object.assign({}, dimensionsRef.current, {
      is_initially_visible: `${isInitiallyVisible.current}`
    }), true);
    if (CardComponentOverride != null && isPrototypeCard(cardData)) {
      return /*#__PURE__*/_jsx(CardComponentOverride, {
        cardData: cardData,
        resolvedData: resolvedDataState,
        usageTracker: usageTracker,
        CardFormatter: DefaultCardFormatter
      });
    }
    let CardComponent;
    try {
      CardComponent = registry.getCardComponent(cardData.definition.cardType, {
        location,
        logging_tag: loggingTag
      });
    } catch (error /** card not found */) {
      // @ts-expect-error this field is purposely left out of the public registry type to avoid
      // external usages
      if (!registry._unsafeIsGlobalRegistry) {
        // if this isn't the global regisry, try again to render the card via our card iframe
        return /*#__PURE__*/_jsx(IframedCrmCard, {
          cardData: cardData,
          CardFormatter: DefaultCardFormatter,
          ActionFormatter: ActionFormatter
        });
      }
      return /*#__PURE__*/_jsx(NotImplementedCard, {
        cardData: cardData,
        CardFormatter: DefaultCardFormatter
      });
    }
    return /*#__PURE__*/_jsx(CardTranslationsLoader, {
      registry: registry,
      cardData: cardData,
      CardFormatter: DefaultCardFormatter,
      children: /*#__PURE__*/_jsx(CardComponent, {
        cardData: cardData,
        resolvedData: resolvedDataState,
        usageTracker: usageTracker,
        CardFormatter: DefaultCardFormatter,
        ActionFormatter: ActionFormatter
      })
    });
  }, [ActionFormatter, CardComponentOverride, cardData, DefaultCardFormatter, exactCardType, location, loggingTag, registry, resolvedDataState, shouldRenderCard, usageTracker]);
  return /*#__PURE__*/_jsx("div", {
    ref: setCardWrapperRef,
    id: `card-wrapper-${cardData.definition.cardId}`,
    "data-test-id": `card-wrapper-${cardData.definition.cardId}`,
    style: {
      height: '100%',
      width: '100%'
    },
    children: /*#__PURE__*/_jsx(CardDataContextProvider, {
      value: cardContextValue,
      children: /*#__PURE__*/_jsx(CardSizeContextProvider, {
        children: /*#__PURE__*/_jsx(CardErrorBoundary, {
          cardData: cardData,
          location: cardLocationContext.location,
          loggingTag: cardLocationContext.loggingTag,
          children: /*#__PURE__*/_jsx(CrmMessagingContext.Provider, {
            value: messagingContextValue,
            children: card
          })
        })
      })
    })
  });
}